import React, { useState } from 'react'
import { Box, Button, Grid, IconButton, Typography, makeStyles } from '@material-ui/core'
import { Form as FinalForm, FormSpy } from 'react-final-form'
import MyTextfield from '../../../control/textfield/textfield'
import MyDatePicker from '../../../control/datepicker/datepicker'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import useTranslate from '../../../i18n/useTranslate'
import { textRequired } from '../../../utilities/validate'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import { useHistory } from 'react-router-dom'

const useStyles = makeStyles((theme) => ({
    root: { borderRadius: 12 },
    card: { padding: theme.spacing(3) },
    sectionTitle: { fontWeight: 600, marginTop: theme.spacing(1), marginBottom: theme.spacing(3) },
    btnGroup: { display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(2), marginTop: theme.spacing(3) },
}))

type TemporaryPasswordFormProps = {
    initialValues?: any
    onSubmit?: (values: any) => void
}

const TemporaryPasswordForm: React.FC<TemporaryPasswordFormProps> = ({ initialValues = {}, onSubmit }) => {
    const classes = useStyles()
    const translate = useTranslate()
    const history = useHistory()

    const [isShowPassword, setIsShowPassword] = useState(false)
    const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false)

    const validateConfirmPassword = (value: string, allValues: any) => {
        if (!value) return translate.get('MESSAGE.M001')
        if (value !== allValues.password) return translate.get('MESSAGE.M019')
        return undefined
    }

    return (
        <FinalForm
            onSubmit={(values) => onSubmit?.(values)}
            initialValues={initialValues}
            render={({ handleSubmit }) => (
                <>
                    <FormSpy subscription={{ values: true }}>
                        {({ values }) => {
                            const startDate: Date | undefined = values?.startDate
                                ? new Date(values.startDate)
                                : undefined

                            return (
                                <Box className={classes.card}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <MyTextfield
                                                name={
                                                    translate.languageStore.currentLanguage === 'th'
                                                        ? 'nameTh'
                                                        : 'nameEn'
                                                }
                                                materialInputProps={{
                                                    label: translate.get('UM.EDIT_RESEARCHER.FULLNAME'),
                                                    placeholder: translate.get('UM.EDIT_RESEARCHER.FULLNAME'),
                                                    size: 'small',
                                                    disabled: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <MyTextfield
                                                name="email"
                                                materialInputProps={{
                                                    label: translate.get('UM.EDIT_RESEARCHER.EMAIL'),
                                                    placeholder: translate.get('UM.EDIT_RESEARCHER.EMAIL'),
                                                    size: 'small',
                                                    disabled: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <MyTextfield
                                                name="phoneNumber"
                                                materialInputProps={{
                                                    label: translate.get('UM.EDIT_RESEARCHER.PHONE'),
                                                    placeholder: translate.get('UM.EDIT_RESEARCHER.PHONE'),
                                                    size: 'small',
                                                    disabled: true,
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Typography className={classes.sectionTitle}>
                                        {translate.get('UM.EDIT_RESEARCHER.PASSWORD_SECTION')}
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <MyTextfield
                                                name="password"
                                                type={isShowPassword ? 'text' : 'password'}
                                                materialInputProps={{
                                                    label: translate.get('UM.EDIT_RESEARCHER.PASSWORD'),
                                                    placeholder: translate.get('UM.EDIT_RESEARCHER.PASSWORD'),
                                                    size: 'small',
                                                }}
                                                icon={
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setIsShowPassword(!isShowPassword)}
                                                    >
                                                        {isShowPassword ? (
                                                            <Visibility style={{ fontSize: '1.2rem' }} />
                                                        ) : (
                                                            <VisibilityOff style={{ fontSize: '1.2rem' }} />
                                                        )}
                                                    </IconButton>
                                                }
                                                validates={[textRequired(translate.get('MESSAGE.M001'))]}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <MyTextfield
                                                name="confirmPassword"
                                                type={isShowConfirmPassword ? 'text' : 'password'}
                                                materialInputProps={{
                                                    label: translate.get('UM.EDIT_RESEARCHER.CONFIRM_PASSWORD'),
                                                    placeholder: translate.get('UM.EDIT_RESEARCHER.CONFIRM_PASSWORD'),
                                                    size: 'small',
                                                }}
                                                icon={
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setIsShowConfirmPassword(!isShowConfirmPassword)}
                                                    >
                                                        {isShowConfirmPassword ? (
                                                            <Visibility style={{ fontSize: '1.2rem' }} />
                                                        ) : (
                                                            <VisibilityOff style={{ fontSize: '1.2rem' }} />
                                                        )}
                                                    </IconButton>
                                                }
                                                validates={[validateConfirmPassword]}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <MyDatePicker
                                                name="startDate"
                                                label={translate.get('UM.EDIT_RESEARCHER.PASSWORD_START_DATE')}
                                                size="small"
                                                icon={<CalendarTodayIcon color="disabled" />}
                                                validates={[textRequired(translate.get('MESSAGE.M001'))]}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <MyDatePicker
                                                name="endDate"
                                                label={translate.get('UM.EDIT_RESEARCHER.PASSWORD_END_DATE')}
                                                size="small"
                                                icon={<CalendarTodayIcon color="disabled" />}
                                                minDate={startDate}
                                                required
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box className={classes.btnGroup}>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => history.push('/manage-researcher')}
                                        >
                                            {translate.get('BUTTON.CANCEL')}
                                        </Button>
                                        <Button variant="contained" color="primary" onClick={handleSubmit}>
                                            {translate.get('BUTTON.CONFIRM')}
                                        </Button>
                                    </Box>
                                </Box>
                            )
                        }}
                    </FormSpy>
                </>
            )}
        />
    )
}

export default TemporaryPasswordForm
